/**
 * Settings cockpit landing: one card per registry category (from
 * `categories()`), each listing its entries' titles + current values (matched to
 * a server SettingRow by key) and a badge describing how the entry changes —
 * `editable ✎`, `PR-gated`, or `operator`. Clicking a card opens the
 * `CategoryEditor` for that category.
 *
 * Current values come from `useSettings()` rows; the registry supplies the
 * static title/type/grouping. Secret entries never show a value (the server
 * returns `value: null` for them and we render a presence dash).
 *
 * Styling is deferred to Task 8; only semantic classNames are emitted here.
 */

import { useState } from 'react';
import { categories, type Category, type SettingEntry } from '../lib/registry';
import { useSettings, type SettingRow } from '../lib/queries';
import CategoryEditor from '../components/CategoryEditor';

/** Compact one-line render of a current value for a card row. */
function fmtValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.length > 60 ? `${value.slice(0, 57)}…` : value;
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  try {
    const s = JSON.stringify(value);
    return s.length > 60 ? `${s.slice(0, 57)}…` : s;
  } catch {
    return String(value);
  }
}

/** Full, untruncated string form of a value — used for the hover tooltip so a
 *  truncated (or relocated-to-hover) value can be read on mouseover. */
function fullValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** "Blob" types whose populated value is a multi-field structure or prose —
 *  showing a truncated preview inline is just clutter, so a populated blob is
 *  relocated to a muted "…" + hover tooltip. (An *empty* container still shows
 *  inline as "[]"/"{}", which is meaningful at a glance.) */
const BLOB_TYPES: ReadonlySet<SettingEntry['type']> = new Set([
  'markdown', 'json', 'list', 'table',
]);

/** True for `[]` or `{}` — an empty container is worth showing inline; a
 *  populated one is a blob best read on hover. */
function isEmptyContainer(value: unknown): boolean {
  if (Array.isArray(value)) return value.length === 0;
  if (value !== null && typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

interface Badge {
  label: string;
  className: string;
}

/** Derive the change-mode badge. The server row's `editable` flag is
 *  authoritative; the `change` field distinguishes PR-gated from operator-only
 *  for non-editable entries. */
function badgeFor(entry: SettingEntry, row: SettingRow | undefined): Badge {
  const editable = row ? row.editable : entry.dashboard === 'editable';
  if (editable) return { label: 'editable ✎', className: 'badge badge-editable' };
  const change = row?.change ?? entry.change;
  if (change === 'pr') {
    return { label: 'PR-gated', className: 'badge badge-pr' };
  }
  // `operator-ssm` / `operator-cli` already say "operator" — prefixing them
  // again ("operator · operator-ssm") doubles the width and overflows cards.
  const label = !change
    ? 'operator'
    : change.startsWith('operator')
      ? change
      : `operator · ${change}`;
  return { label, className: 'badge badge-operator' };
}

export default function SettingsTab() {
  const { data, isLoading, isError, error } = useSettings();
  const [openPrefix, setOpenPrefix] = useState<string | null>(null);

  const cats = categories();
  const rows = data?.settings ?? [];
  const rowByKey = new Map<string, SettingRow>();
  for (const r of rows) rowByKey.set(r.key, r);

  if (isLoading) {
    return <p className="settings-loading">Loading settings…</p>;
  }
  if (isError) {
    return (
      <p className="settings-error error" role="alert">
        Failed to load settings: {error instanceof Error ? error.message : 'unknown error'}
      </p>
    );
  }

  const openCat: Category | undefined = openPrefix
    ? cats.find((c) => c.prefix === openPrefix)
    : undefined;

  if (openCat) {
    return (
      <CategoryEditor
        category={openCat}
        rows={rows}
        onBack={() => setOpenPrefix(null)}
      />
    );
  }

  return (
    <div className="settings-tab">
      <div className="settings-cards">
        {cats.map((cat) => (
          <button
            type="button"
            key={cat.prefix}
            className="settings-card"
            aria-label={cat.title}
            onClick={() => setOpenPrefix(cat.prefix)}
          >
            <h3 className="settings-card-title">{cat.title}</h3>
            <ul className="settings-card-entries">
              {cat.entries
                .filter((entry) => entry.dashboard !== 'hidden')
                .map((entry) => {
                const row = rowByKey.get(entry.key);
                const badge = badgeFor(entry, row);
                const raw = row?.value;
                const full = entry.secret ? '' : fullValue(raw);
                const hasContent = full.length > 0;
                // A populated blob (markdown prose, JSON/list/table) is
                // relocated to a muted "…" + hover tooltip; scalars, short
                // strings, and empty containers render inline as before.
                const isPopulatedBlob =
                  hasContent && BLOB_TYPES.has(entry.type) && !isEmptyContainer(raw);
                const inline = entry.secret
                  ? '••••'
                  : !hasContent
                    ? '—'
                    : isPopulatedBlob
                      ? '…'
                      : fmtValue(raw);
                // Offer the full value on hover whenever it isn't fully shown
                // inline (relocated blobs and CSS/length-truncated values).
                const tip = !entry.secret && hasContent && inline !== full ? full : undefined;
                return (
                  <li
                    className="settings-card-entry"
                    key={entry.key}
                    data-key={entry.key}
                    title={tip}
                  >
                    <span className="entry-title">{entry.title}</span>
                    <span className="entry-value">{inline}</span>
                    <span className={badge.className}>{badge.label}</span>
                  </li>
                );
              })}
            </ul>
          </button>
        ))}
      </div>
    </div>
  );
}
